/**
 * AlertsPage — Sprint 21.5 in-app mirror of Telegram alerts.
 *
 * P13-compliant: summary strip → filter bar → enriched alert feed →
 * track record. Uses alerts_log payload for full enrichment (asset
 * description, current price, trajectory, interpretation).
 */

import { useState, useEffect } from 'react';
import { Bell, Filter, TrendingUp, TrendingDown, Zap, AlertTriangle, Brain, Target, Check } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

const TYPE_META = {
  prediction:       { Icon: Brain,          color: '#3b82f6', label: 'PREDICTION' },
  recommendation:   { Icon: Target,         color: '#10b981', label: 'RECOMMENDATION' },
  emergency_cycle:  { Icon: Zap,            color: '#ef4444', label: 'EMERGENCY' },
  resolution:       { Icon: Check,          color: '#8b5cf6', label: 'RESOLVED' },
  correct:          { Icon: Check,          color: '#10b981', label: 'CORRECT' },
  wrong:            { Icon: AlertTriangle,  color: '#ef4444', label: 'WRONG' },
  digest:           { Icon: Bell,           color: '#6b7280', label: 'DIGEST' },
  regime_change:    { Icon: TrendingUp,     color: '#f59e0b', label: 'REGIME' },
};

const LAST_READ_KEY = 'korinth_alerts_last_read';

function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function fmtPct(x) {
  if (x == null || !Number.isFinite(x)) return '—';
  return `${x >= 0 ? '+' : ''}${x.toFixed(2)}%`;
}

// Sprint 21.6b helpers
const DIRECTION_CIRCLE = {
  bullish: '🟢', buy: '🟢', long: '🟢', add: '🟢', accumulate: '🟢',
  bearish: '🔴', sell: '🔴', short: '🔴', reduce: '🔴', trim: '🔴',
  neutral: '🟡', hold: '🟡', flat: '🟡',
  correct: '🟢', wrong: '🔴', partial: '🟡', hit: '🟢', miss: '🔴',
};
const DIRECTION_ARROW = {
  bullish: '▲', buy: '▲', long: '▲', add: '▲', accumulate: '▲',
  bearish: '▼', sell: '▼', short: '▼', reduce: '▼', trim: '▼',
  neutral: '◆', hold: '◆', flat: '◆',
};
const circle = (d) => DIRECTION_CIRCLE[(d || '').toLowerCase()] || '🟡';
const arrow = (d) => DIRECTION_ARROW[(d || '').toLowerCase()] || '◆';

function KindBadge({ kind }) {
  if (kind === 'target') return <span className="text-[8px] font-mono font-bold px-1 py-0.5 rounded bg-korinth-accent/15 text-korinth-accent">TARGET</span>;
  if (kind === 'directional') return <span className="text-[8px] font-mono font-bold px-1 py-0.5 rounded bg-blue-500/15 text-blue-400">DIRECTIONAL</span>;
  return null;
}

// Sprint 21.7.3: severity tier badge on each alert row
const SEVERITY_BADGE = {
  critical:       { emoji: '🚨', label: 'Critical', cls: 'bg-red-500/15 text-red-400' },
  daily_digest:   { emoji: '🌅', label: 'Daily',    cls: 'bg-yellow-500/15 text-yellow-400' },
  weekly_digest:  { emoji: '📊', label: 'Weekly',   cls: 'bg-purple-500/15 text-purple-400' },
  desktop_only:   { emoji: '💻', label: 'Desktop',  cls: 'bg-korinth-border/30 text-korinth-muted' },
};
function SeverityBadge({ tier }) {
  const m = SEVERITY_BADGE[tier];
  if (!m) return null;
  return (
    <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${m.cls}`} title={`Severity tier: ${tier}`}>
      {m.emoji} {m.label}
    </span>
  );
}

function AlertCard({ alert, isUnread }) {
  const meta = TYPE_META[alert.alert_type] || TYPE_META.digest;
  const Icon = meta.Icon;
  const p = alert.payload || {};
  // Sprint 21.6b — use payload circle/arrow if present, fall back to derivation
  const dirCircle = p.direction_circle || p.outcome_circle || circle(p.direction || p.outcome);
  const dirArrow = p.direction_arrow || arrow(p.direction);
  const kind = p.prediction_kind || p.recommendation_kind;

  return (
    <div className={`bg-korinth-surface border rounded-lg p-4 transition-colors ${isUnread ? 'border-l-4 border-l-korinth-accent border-korinth-border' : 'border-korinth-border'}`}>
      {/* Header row */}
      <div className="flex items-start gap-3 mb-2">
        <div className="flex-shrink-0 mt-0.5">
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: meta.color, backgroundColor: meta.color + '15' }}>
              {meta.label}
            </span>
            {kind && <KindBadge kind={kind} />}
            {(p.direction || p.outcome) && (
              <span className="text-base leading-none" title={p.direction || p.outcome}>{dirCircle}</span>
            )}
            {alert.asset && (
              <span className="text-sm font-mono font-bold text-white">{alert.asset}</span>
            )}
            {p.asset_description?.name && (
              <span className="text-[10px] font-mono text-korinth-muted">· {p.asset_description.name}</span>
            )}
            {/* Sprint 21.6g: Telegram delivery indicator */}
            {alert.pushed_to_telegram ? (
              <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300" title="Delivered to Telegram">
                📱 Pushed
              </span>
            ) : null}
            {/* Sprint 21.7.3: severity tier badge */}
            {alert.severity_tier && (
              <SeverityBadge tier={alert.severity_tier} />
            )}
            <span className="text-[9px] font-mono text-korinth-muted ml-auto">{timeAgo(alert.sent_at)}</span>
          </div>
          {/* Sprint 21.7.3: show suppressed_reason inline so /alerts explains why a row didn't go to Telegram */}
          {alert.suppressed_reason && !alert.pushed_to_telegram && (
            <div className="text-[9px] font-mono text-korinth-muted mt-0.5 italic" title="Reason this alert did not fire to Telegram">
              Suppressed: {alert.suppressed_reason}
            </div>
          )}
          {p.asset_description?.description && (
            <div className="text-[10px] font-mono text-korinth-muted italic mt-0.5">{p.asset_description.description}</div>
          )}
        </div>
      </div>

      {/* Prediction payload */}
      {alert.alert_type === 'prediction' && (
        <div className="space-y-1.5 text-[11px] font-mono">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={p.direction === 'bullish' ? 'text-korinth-accent font-bold' : p.direction === 'bearish' ? 'text-korinth-danger font-bold' : 'text-korinth-warning font-bold'}>
              {dirArrow} {p.direction?.toUpperCase()}
            </span>
            <span className="text-korinth-text">({p.timeframe})</span>
            <span className="text-korinth-accent font-bold">Confidence: {p.confidence}%</span>
            {p.trajectory && (
              <span className="text-[10px] text-korinth-muted">(was {p.trajectory.previous}%, {p.trajectory.arrow})</span>
            )}
          </div>
          {kind === 'target' ? (
            <>
              {(p.current_price != null || p.target_price != null) && (
                <div className="text-korinth-text">
                  {p.current_price != null && <span>Current: ${p.current_price.toFixed(2)}</span>}
                  {p.current_price != null && p.target_price != null && <span> → </span>}
                  {p.target_price != null && <span>Target: ${p.target_price.toFixed(2)}</span>}
                  {p.target_delta_pct != null && <span className={p.target_delta_pct >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}> ({fmtPct(p.target_delta_pct)})</span>}
                </div>
              )}
              {p.invalidation_price != null && p.current_price != null && (
                <div className="text-korinth-muted">Stop: ${p.invalidation_price.toFixed(2)} ({fmtPct(((p.invalidation_price - p.current_price) / p.current_price) * 100)})</div>
              )}
            </>
          ) : kind === 'directional' ? (
            <>
              {p.current_price != null && (
                <div className="text-korinth-text">Current: ${p.current_price.toFixed(2)}</div>
              )}
              <div className="bg-blue-500/10 border-l-2 border-l-blue-400 rounded px-2 py-1 text-blue-300 font-bold">
                Directional call — no price target set
              </div>
              <div className="text-[10px] text-korinth-muted italic">
                (direction conviction only, over {p.timeframe || 'swing'} horizon)
              </div>
            </>
          ) : (
            // Legacy payloads without kind — render whatever's there
            (p.current_price != null || p.target_price != null) && (
              <div className="text-korinth-text">
                {p.current_price != null && <span>Current: ${p.current_price.toFixed(2)}</span>}
                {p.current_price != null && p.target_price != null && <span> → </span>}
                {p.target_price != null && <span>Target: ${p.target_price.toFixed(2)}</span>}
              </div>
            )
          )}
          {p.thesis && <div className="text-korinth-text italic pt-1">{p.thesis}</div>}
          {p.interpretation && (
            <div className="bg-korinth-accent/5 border-l-2 border-l-korinth-accent rounded px-2 py-1 mt-1 text-korinth-text">
              💡 {p.interpretation}
            </div>
          )}
        </div>
      )}

      {/* Recommendation payload */}
      {alert.alert_type === 'recommendation' && (
        <div className="space-y-1.5 text-[11px] font-mono">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={['buy','bullish','add','accumulate'].includes(p.direction) ? 'text-korinth-accent font-bold' : ['sell','bearish','reduce','trim','short'].includes(p.direction) ? 'text-korinth-danger font-bold' : 'text-korinth-warning font-bold'}>
              {dirArrow} {(p.direction || '').toUpperCase()}
            </span>
            <span className="text-korinth-accent font-bold">Conviction: {p.conviction?.toFixed(1)}/10</span>
            {p.trajectory && (
              <span className="text-[10px] text-korinth-muted">(was {p.trajectory.previous.toFixed(1)}/10, {p.trajectory.arrow})</span>
            )}
            {p.timeframe && <span className="text-korinth-text">· {p.timeframe.toUpperCase()}</span>}
          </div>
          {kind === 'directional' ? (
            <>
              {p.current_price != null && <div className="text-korinth-text">Current: ${p.current_price.toFixed(2)}</div>}
              <div className="bg-blue-500/10 border-l-2 border-l-blue-400 rounded px-2 py-1 text-blue-300 font-bold">
                Directional call — no price target set
              </div>
            </>
          ) : (p.current_price != null || p.target_price != null) && (
            <div className="text-korinth-text">
              {p.current_price != null && <span>Current: ${p.current_price.toFixed(2)}</span>}
              {p.target_price != null && <span> → Target: ${p.target_price.toFixed(2)}</span>}
            </div>
          )}
          {p.position_size_pct && <div className="text-korinth-muted">Sized: {p.position_size_pct}% of portfolio</div>}
          {p.reasoning && <div className="text-korinth-text italic pt-1">{p.reasoning}</div>}
          {p.interpretation && (
            <div className="bg-korinth-accent/5 border-l-2 border-l-korinth-accent rounded px-2 py-1 mt-1 text-korinth-text">
              💡 Why now: {p.interpretation}
            </div>
          )}
        </div>
      )}

      {/* Resolution payload (correct / wrong / partial / resolution) */}
      {['correct','wrong','partial','resolution'].includes(alert.alert_type) && (
        <div className="space-y-1.5 text-[11px] font-mono">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={alert.alert_type === 'correct' ? 'text-korinth-accent font-bold' : alert.alert_type === 'wrong' ? 'text-korinth-danger font-bold' : 'text-korinth-warning font-bold'}>
              {dirArrow} {p.direction?.toUpperCase()}
            </span>
            <span className="text-[10px] text-korinth-muted">Confidence at issue: {p.confidence}%</span>
            {p.pnl_pct != null && (
              <span className={p.pnl_pct >= 0 ? 'text-korinth-accent font-bold' : 'text-korinth-danger font-bold'}>P&L: {fmtPct(p.pnl_pct)}</span>
            )}
          </div>
          {p.entry_price && p.resolved_price && (
            <div className="text-korinth-text">Entry: ${p.entry_price.toFixed(2)} → Resolved: ${p.resolved_price.toFixed(2)}</div>
          )}
          {p.prediction_kind === 'directional' && (
            <div className="text-[10px] text-blue-300 italic">Directional call — no target was set</div>
          )}
          {p.days != null && <div className="text-[10px] text-korinth-muted">Days open: {p.days}</div>}
        </div>
      )}

      {/* Emergency cycle payload */}
      {alert.alert_type === 'emergency_cycle' && (
        <div className="space-y-1.5 text-[11px] font-mono">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-korinth-danger/15 text-korinth-danger">L{p.priority}</span>
            <span className="text-white font-bold">{p.source}</span>
            {p.credibility != null && (
              <span className="text-[10px] text-korinth-muted">{p.credibility_basis === 'themis_trust' ? `THEMIS trust ${Math.round(p.credibility)}/100` : `credibility: ${Math.round(p.credibility)}%`}</span>
            )}
          </div>
          {p.headline && <div className="text-korinth-text">🚨 {p.headline}</div>}
          {p.interpretation && (
            <div className="bg-korinth-warning/5 border-l-2 border-l-korinth-warning rounded px-2 py-1 mt-1 text-korinth-text">
              💡 {p.interpretation}
            </div>
          )}
          {p.result && (
            <div className="text-korinth-muted pt-1">
              Cycle: {p.result.observationsGenerated || 0} obs · {p.result.predictionsCreated || 0} preds created · {p.result.predictionsUpdated || 0} updated
            </div>
          )}
        </div>
      )}

      {/* Fallback: show raw body for types we don't have custom rendering for */}
      {!['prediction', 'recommendation', 'emergency_cycle'].includes(alert.alert_type) && (
        <pre className="text-[10px] font-mono text-korinth-text whitespace-pre-wrap leading-relaxed">
          {alert.body}
        </pre>
      )}
    </div>
  );
}

export default function AlertsPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('alerts'); }, []);

  const [data, setData] = useState({ alerts: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Sprint 21.7.3: severity tier filter (client-side; backend returns all)
  const [tier, setTier] = useState('all');
  const [delivery, setDelivery] = useState('all');  // Sprint 21.6g: pushed / desktop / all
  const [lastRead, setLastRead] = useState(() => localStorage.getItem(LAST_READ_KEY) || new Date(0).toISOString());
  const { track, loading: trackLoading } = usePageTracking('signals');

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const r = await fetch(`/api/alerts?limit=100&filter=${filter}&delivery=${delivery}`);
        if (r.ok) setData(await r.json());
      } catch {}
      setLoading(false);
    }
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [filter, delivery]);

  function markAllRead() {
    const now = new Date().toISOString();
    localStorage.setItem(LAST_READ_KEY, now);
    setLastRead(now);
  }

  const alerts = data.alerts || [];
  const summary = data.summary || {};
  const unreadCount = alerts.filter(a => new Date(a.sent_at).getTime() > new Date(lastRead).getTime()).length;

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'predictions', label: 'Predictions' },
    { key: 'recommendations', label: 'Recommendations' },
    { key: 'emergencies', label: 'Emergencies' },
    { key: 'resolutions', label: 'Resolved' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Bell className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Alerts</h1>
        <span className="text-korinth-muted text-sm font-mono">In-app mirror of Telegram — enriched with asset description + price + trajectory + interpretation</span>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className="ml-auto text-[10px] font-mono px-3 py-1.5 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
            Mark {unreadCount} read
          </button>
        )}
      </div>

      {/* Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Unread:</span> <span className="text-korinth-accent font-bold">{unreadCount}</span></span>
        <span><span className="text-korinth-muted">Predictions today:</span> <span className="text-white font-bold">{summary.today_by_type?.prediction || 0}</span></span>
        <span><span className="text-korinth-muted">Recommendations today:</span> <span className="text-white font-bold">{summary.today_by_type?.recommendation || 0}</span></span>
        <span><span className="text-korinth-muted">Emergencies this week:</span> <span className="text-korinth-warning font-bold">{summary.week_by_type?.emergency_cycle || 0}</span></span>
        <span><span className="text-korinth-muted">Total (7d):</span> <span className="text-korinth-text">{summary.total_week || 0}</span></span>
      </div>

      {/* Filter bars */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-korinth-muted" />
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-[10px] font-mono px-3 py-1.5 rounded border transition-colors ${filter === f.key ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent font-bold' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Sprint 21.6g: delivery filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider">Delivery:</span>
          {[
            { key: 'all',     label: 'All',                      subtitle: null },
            { key: 'pushed',  label: '📱 Pushed to Telegram',    subtitle: summary.pushed },
            { key: 'desktop', label: 'Desktop only',              subtitle: summary.desktop },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setDelivery(f.key)}
              className={`text-[10px] font-mono px-3 py-1.5 rounded border transition-colors ${delivery === f.key ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent font-bold' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}
              style={{ minHeight: 36 }}
            >
              {f.label}{f.subtitle != null && <span className="ml-1 text-[9px] opacity-70">({f.subtitle})</span>}
            </button>
          ))}
        </div>

        {/* Sprint 21.7.3: severity tier filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider">Tier:</span>
          {[
            { key: 'all',             label: 'All' },
            { key: 'critical',        label: '🚨 Critical' },
            { key: 'daily_digest',    label: '🌅 Daily' },
            { key: 'weekly_digest',   label: '📊 Weekly' },
            { key: 'desktop_only',    label: '💻 Desktop' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setTier(f.key)}
              className={`text-[10px] font-mono px-3 py-1.5 rounded border transition-colors ${tier === f.key ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent font-bold' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}
              style={{ minHeight: 36 }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Feed */}
      {(() => {
        const filteredByTier = tier === 'all' ? alerts : alerts.filter(a => (a.severity_tier || 'desktop_only') === tier);
        return (
          <ModuleContainer title="Recent Alerts" subtitle={loading ? 'Loading...' : `${filteredByTier.length} in last 7 days`}>
            {filteredByTier.length === 0 && !loading ? (
              <div className="text-[11px] font-mono text-korinth-muted text-center py-6 italic">
                No alerts in the last 7 days for this filter.
                {(filter !== 'all' || tier !== 'all') && (
                  <div className="mt-1">
                    Try <button onClick={() => { setFilter('all'); setTier('all'); }} className="text-korinth-accent hover:underline">All</button>.
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredByTier.map(a => (
                  <AlertCard key={a.id} alert={a} isUnread={new Date(a.sent_at).getTime() > new Date(lastRead).getTime()} />
                ))}
              </div>
            )}
          </ModuleContainer>
        );
      })()}

      {/* Track record */}
      <TrackRecordPanel {...track} loading={trackLoading} metric_name="Alert Signal Quality" />
    </div>
  );
}
